import { Box, Typography, Paper, Link } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatPhoneDisplay } from '../../../utils/phoneUtils';

/**
 * Validates contact data based on validation rules
 * @param {Object} contact - Contact object to validate
 * @param {Object} validationRules - Validation rules configuration
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
const validateContact = (contact, validationRules = {}) => {
  const errors = [];

  if (!contact) {
    if (
      validationRules.requireName ||
      validationRules.requireEmail ||
      validationRules.requireEmailOrPhone ||
      validationRules.requireAddress
    ) {
      errors.push('Contact is required');
    }
    return { isValid: errors.length === 0, errors };
  }

  // Name validation
  if (validationRules.requireName) {
    const hasName = !!(contact.name || contact.business_name);
    if (!hasName) {
      errors.push('Name is required');
    }
  }

  // Email OR Phone validation (at least one required)
  if (validationRules.requireEmailOrPhone) {
    const hasEmail = !!contact.email;
    const hasPhone = !!contact.phone1;
    if (!hasEmail && !hasPhone) {
      errors.push('Email or phone is required');
    }
  }

  // Email only validation
  if (validationRules.requireEmail) {
    const hasEmail = !!contact.email;
    if (!hasEmail) {
      errors.push('Email is required');
    }
  }

  // Email AND Phone validation (both required)
  if (validationRules.requireEmailAndPhone) {
    const hasEmail = !!contact.email;
    const hasPhone = !!contact.phone1;
    if (!hasEmail) {
      errors.push('Email is required');
    }
    if (!hasPhone) {
      errors.push('Phone is required');
    }
  }

  // Full address validation
  if (validationRules.requireAddress) {
    const hasAddress = !!(contact.address || contact.full_address_display);
    const hasCity = !!contact.city;
    const hasState = !!contact.state;
    const hasZip = !!(contact.zipcode || contact.zip); // Backend uses 'zipcode', some forms use 'zip'

    if (!hasAddress) errors.push('Address is required');
    if (!hasCity) errors.push('City is required');
    if (!hasState) errors.push('State is required');
    if (!hasZip) errors.push('ZIP code is required');
  }

  return { isValid: errors.length === 0, errors };
};

/**
 * AddressDisplayCard - Compact view mode for selected contact with built-in validation
 *
 * @param {Object} props
 * @param {Object} props.contact - Selected contact object
 * @param {function} props.onChange - Handler to open address book modal
 * @param {function} props.onChangeClick - Alias for onChange
 * @param {function} props.onEdit - Handler to enable inline editing
 * @param {boolean} props.required - Is this field required?
 * @param {boolean} props.error - Show error state? (legacy - use validationRules instead)
 * @param {string} props.label - Field label (e.g., "Shipping Address", "Billing Address")
 * @param {string} props.title - Alias for label
 * @param {string} props.helperText - Helper text to display below the card
 * @param {boolean} props.showAddress - Whether to show address fields (default: true)
 * @param {boolean} props.disabled - Disable the change/select buttons
 * @param {Object} props.validationRules - Validation rules configuration
 * @param {boolean} props.validationRules.requireName - Require name or business_name
 * @param {boolean} props.validationRules.requireEmail - Require email only
 * @param {boolean} props.validationRules.requireEmailOrPhone - Require email OR phone (at least one)
 * @param {boolean} props.validationRules.requireEmailAndPhone - Require both email AND phone
 * @param {boolean} props.validationRules.requireAddress - Require full address fields
 * @param {function} props.onValidationChange - Callback when validation state changes: (isValid, errors) => void
 * @param {string} props.emptyText - Custom text to show when no contact is selected (overrides default)
 * @param {string} props.emptyClickText - Custom text for the clickable link when empty (overrides default)
 */
const AddressDisplayCard = ({
  contact,
  onChange,
  onChangeClick,
  onEdit,
  required = false,
  error = false,
  label,
  title,
  helperText,
  showAddress = true,
  disabled = false,
  validationRules = null,
  onValidationChange = null,
  addressOnlyMode = false, // When true, shows address-specific text instead of contact text
  emptyText = null, // Custom empty state text
  emptyClickText = null, // Custom click link text
  hideChangeLink = false, // When true, hide the (change) link - for use-once-only scenarios without address book
}) => {
  const { t } = useTranslation();
  const [validationState, setValidationState] = useState({ isValid: true, errors: [] });
  const [hasInteracted, setHasInteracted] = useState(false);

  // Perform validation when contact changes (NOT validationRules to avoid infinite loops)
  useEffect(() => {
    if (validationRules) {
      const result = validateContact(contact, validationRules);

      // Only update if validation result actually changed
      if (
        result.isValid !== validationState.isValid ||
        JSON.stringify(result.errors) !== JSON.stringify(validationState.errors)
      ) {
        setValidationState(result);

        // Notify parent component of validation state change
        if (onValidationChange) {
          onValidationChange(result.isValid, result.errors);
        }
      }
    }
  }, [contact]); // Only depend on contact, not validationRules (which is recreated on every render)

  const handleClick = () => {
    setHasInteracted(true);
    const handler = onChange || onChangeClick;
    if (handler) handler();
  };

  const handleEdit = contact => {
    setHasInteracted(true);
    if (onEdit) onEdit(contact);
  };

  const displayLabel = label || title;

  // Check if contact is empty - include address check for address-only mode
  const hasContactInfo = contact && (contact.name || contact.business_name || contact.email || contact.phone1);
  const hasAddressInfo = contact && (contact.address || contact.city || contact.full_address_display);
  const isEmpty = !hasContactInfo && !hasAddressInfo;

  // Construct full_address_display if not provided but individual fields exist
  const getAddressDisplay = () => {
    // Check if full_address_display exists and has meaningful content (not just commas/spaces)
    const existingDisplay = contact?.full_address_display?.trim();
    if (existingDisplay && existingDisplay.replace(/[,\s]/g, '').length > 0) {
      return existingDisplay;
    }
    if (!contact) return null;

    const parts = [];
    // Use trim and check for truthy to handle empty strings
    if (contact.address?.trim()) parts.push(contact.address.trim());
    if (contact.address_2?.trim()) parts.push(contact.address_2.trim());
    if (contact.city?.trim()) parts.push(contact.city.trim());
    const state = contact.state?.trim();
    const zipcode = contact.zipcode?.trim();
    if (state && zipcode) {
      parts.push(`${state} ${zipcode}`);
    } else if (state) {
      parts.push(state);
    } else if (zipcode) {
      parts.push(zipcode);
    }
    if (contact.country?.trim()) {
      parts.push(contact.country.trim());
    }

    return parts.length > 0 ? parts.join(', ') : null;
  };

  const addressDisplay = getAddressDisplay();

  // Show error if:
  // 1. External error prop is true (e.g., from Formik validation)
  // 2. Legacy mode: required && error && isEmpty
  // 3. New validation mode: validationRules provided and validation failed AND user has interacted
  const hasExternalError = error && helperText; // External error with message (from Formik, etc.)
  const hasInternalError = validationRules ? !validationState.isValid && hasInteracted : required && error && isEmpty;
  const showError = hasExternalError || hasInternalError;

  return (
    <Box sx={{ mb: 2 }}>
      {displayLabel && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {displayLabel}
          {required && (
            <Typography component="span" color="error">
              *
            </Typography>
          )}
        </Typography>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderColor: showError ? 'error.main' : 'divider',
          borderWidth: showError ? 2 : 1,
          backgroundColor: isEmpty ? 'action.hover' : 'background.paper',
        }}
      >
        {isEmpty ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body2" color={showError ? 'error' : 'text.secondary'} sx={{ mb: 1 }}>
              {showError
                ? t('contacts.requiredFieldSelectAddress')
                : emptyText ||
                  (addressOnlyMode
                    ? t('contacts.noAddressSelectedAddress', 'No address selected')
                    : t('contacts.noAddressSelected'))}
            </Typography>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={handleClick}
              disabled={disabled}
              sx={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                opacity: disabled ? 0.5 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
              }}
            >
              {emptyClickText ||
                (addressOnlyMode
                  ? t('contacts.clickToSelectAddress', 'Click to select an address')
                  : t('contacts.clickToSelectContact'))}
            </Link>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                {/* Show contact name/business if available, otherwise show address as primary */}
                {hasContactInfo ? (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {contact.name || contact.business_name}
                    </Typography>
                    {contact.name && contact.business_name && (
                      <Typography variant="body2" color="text.secondary">
                        {contact.business_name}
                      </Typography>
                    )}
                  </>
                ) : (
                  // Address-only mode: show business name (if available) and address as primary content
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {contact.business_name || addressDisplay || t('contacts.addressSelected', 'Address')}
                    </Typography>
                    {contact.business_name && addressDisplay && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {addressDisplay}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
                {/* Show change button to allow going back to address book (unless hideChangeLink is true) */}
                {!hideChangeLink && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleClick}
                    disabled={disabled}
                    sx={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      pointerEvents: disabled ? 'none' : 'auto',
                    }}
                  >
                    ({t('contacts.change')})
                  </Link>
                )}
                {/* Show edit button when handler is provided */}
                {onEdit && (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => handleEdit(contact)}
                    disabled={disabled}
                    sx={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      pointerEvents: disabled ? 'none' : 'auto',
                    }}
                  >
                    ({t('contacts.edit', 'edit')})
                  </Link>
                )}
              </Box>
            </Box>

            {/* Show email/phone only when we have contact info */}
            {hasContactInfo && (contact.email?.trim() || contact.phone1?.trim()) && (
              <Typography variant="body2" color="text.secondary">
                {[contact.email?.trim(), contact.phone1 ? formatPhoneDisplay(contact.phone1) : null]
                  .filter(Boolean)
                  .join(' | ')}
              </Typography>
            )}

            {/* Show address as secondary info when we have contact info, skip if address-only (already shown above) */}
            {showAddress && addressDisplay && hasContactInfo && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {addressDisplay}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Show helper text when not in error state */}
      {helperText && !showError && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}
      {/* Show error message - prioritize external errors (Formik), then internal validation errors */}
      {showError && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {hasExternalError
            ? helperText
            : validationRules && validationState.errors.length > 0
              ? validationState.errors.join(', ')
              : t('contacts.pleaseSelectAddress')}
        </Typography>
      )}
    </Box>
  );
};

export default AddressDisplayCard;
